/**
 * Ticket Picker Modal - Zendesk-style "Open in new tab"
 * Browse/search tickets and open in new tab
 */

import React, { useState } from 'react';
import { Modal, Input, Table, Typography, Tag, Spin, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import api from '../config/api';
import { useAgentWorkspaceTabs } from '../contexts/AgentWorkspaceTabsContext';
import dayjs from 'dayjs';

interface TicketPickerModalProps {
  open: boolean;
  onClose: () => void;
}

export const TicketPickerModal: React.FC<TicketPickerModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const tabsContext = useAgentWorkspaceTabs();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const { data, isLoading } = useQuery(
    ['ticket-picker', searchTerm, page],
    () =>
      api.get('/api/tickets', {
        params: {
          search: searchTerm || undefined,
          page,
          limit: pageSize,
        },
      }).then((r) => r.data),
    { enabled: open }
  );

  const tickets = data?.tickets || [];
  const total = data?.total || 0;

  const handleSearch = () => {
    setSearchTerm(searchInput.trim());
    setPage(1);
  };

  const handleSelectTicket = (record: any) => {
    if (!tabsContext) return;
    const ticketNumber = record.ticket_number;
    const tabId = `number/${ticketNumber}`;
    const label = `#${ticketNumber}`;
    tabsContext.addTab(tabId, label);
    navigate(`/workspace/number/${ticketNumber}`, { state: {} });
    onClose();
    setSearchInput('');
    setSearchTerm('');
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'ticket_number',
      key: 'ticket_number',
      width: 90,
      render: (text: string) => (
        <Typography.Text strong style={{ fontFamily: 'monospace' }}>
          #{text}
        </Typography.Text>
      ),
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
      render: (text: string) => (
        <Typography.Text ellipsis title={text}>
          {text || '—'}
        </Typography.Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: string) => (
        <Tag color={status === 'solved' ? 'green' : status === 'pending' ? 'orange' : status === 'open' ? 'blue' : 'default'}>
          {status?.toUpperCase() || '—'}
        </Tag>
      ),
    },
    {
      title: 'Business',
      dataIndex: 'business_name',
      key: 'business_name',
      width: 140,
      ellipsis: true,
      render: (t: string) => t || '—',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 110,
      render: (d: string) => dayjs(d).format('MMM D, YYYY'),
    },
  ];

  return (
    <Modal
      title="Open ticket in new tab"
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnClose
    >
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        <Input
          placeholder="Search tickets by number, subject, business..."
          prefix={<SearchOutlined />}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={handleSearch}
          allowClear
          style={{ maxWidth: 400 }}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
          Search
        </Button>
        <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
          Select a ticket to open in a new tab
        </Typography.Text>
      </div>
      {isLoading ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          dataSource={tickets}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            showTotal: (t) => `Total ${t} tickets`,
            onChange: setPage,
          }}
          onRow={(record) => ({
            onClick: () => handleSelectTicket(record),
            style: { cursor: 'pointer' },
          })}
          scroll={{ y: 360 }}
        />
      )}
    </Modal>
  );
};
